import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DataResponse } from '../../../core/models/data-response';
import { ConfigService } from '../../../core/services/config/config.service';
import { UtilityService } from '../../../core/services/utility/utility.service';
import { Message } from '../../models/message';
import { MessageRequest } from '../../models/message-request';

@Injectable({
  providedIn: 'root',
})
export class MessagesService {
  private utilityService = inject(UtilityService);
  private configService = inject(ConfigService);
  private config = this.configService.config;

  messagesEndpoint = `${this.config.patientApiUrl}/v1/PatientPortal/Messages`;

  postMessages(
    patientId: number,
    pageNumber: number | undefined,
    pageSize: number | undefined
  ): Observable<DataResponse<Message[]>> {
    const searchParams = {
      pageNumber,
      pageSize,
    };
    return this.utilityService.postRequest<DataResponse<Message[]>>(
      `${this.messagesEndpoint}/Search?patientId=${patientId}`,
      searchParams,
      // Reads opt out of the global error page: MessagesComponent renders app-error-state with a
      // retry. postMessage below is a genuine mutation and deliberately stays on the global path.
      { rawResponse: true, skipGlobalError: true, applyDefaultTimeout: true }
    );
  }

  postMessage(patientId: number, messageRequest: MessageRequest): Observable<Message> {
    return this.utilityService.postRequest<Message>(`${this.messagesEndpoint}?patientId=${patientId}`, messageRequest);
  }

  getMessages(patientId: number): Observable<Message[]> {
    // Deliberately NOT opted out of the global error page. Despite the name, this is the THREAD read:
    // its only caller is the getMessageResponses$ effect, for a message not already in the store. The
    // reply and view-only templates render no error state, so opting it out would make a failed thread
    // open silently do nothing — which is exactly what ADR-014 forbids ("do not opt out a read whose
    // screen has no error state of its own"). The LIST read is postMessages above.
    // Opt this out only together with an error state in the thread views (wave 2 / PBI 56790).
    return this.utilityService.getRequest<Message[]>(
      `${this.messagesEndpoint}?patientId=${patientId}&sortDirection=2&includeHistory=true`
    );
  }
}
